import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosClientSignIn } from "../axios/api.js";

// 1. Fetch subtasks
export const fetchSubtasks = createAsyncThunk(
  "subtasks/fetchSubtasks",
  async (taskId, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get(`/tasks/${taskId}/subtasks`);
      return { taskId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch subtasks");
    }
  }
);

// 1.5 Fetch Task Activity (Logs)
export const fetchTaskActivity = createAsyncThunk(
  "subtasks/fetchTaskActivity",
  async (taskId, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.get(`/tasks/${taskId}/activity`);
      return { taskId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch task activity");
    }
  }
);

// 2. Create subtask
export const createSubtask = createAsyncThunk(
  "subtasks/createSubtask",
  async ({ taskId, title, startDate, endDate, assigneeId }, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.post(`/tasks/${taskId}/subtasks`, { title, startDate, endDate, assigneeId });
      return { taskId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to create subtask");
    }
  }
);

// 3. Toggle subtask
export const toggleSubtask = createAsyncThunk(
  "subtasks/toggleSubtask",
  async ({ taskId, subtaskId, isCompleted }, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.put(`/tasks/subtasks/${subtaskId}/toggle`, { isCompleted });
      console.log( "subtasks/toggleSubtask", "subtasks/toggleSubtask", "subtasks/toggleSubtask",)
      return { taskId, subtaskId, isCompleted, data: response.data };
    } catch (error) {
      console.log(error,"errorerrorerrorerrorerrorerrorerrorerrorerrorerror")
      return rejectWithValue(error.response?.data?.message || "Failed to toggle subtask");
    }
  }
);

// 4. Reorder subtasks
export const reorderSubtasks = createAsyncThunk(
  "subtasks/reorderSubtasks",
  async ({ taskId, subtaskIds }, { rejectWithValue }) => {
    try {
      await axiosClientSignIn.put(`/tasks/${taskId}/subtasks/reorder`, { subtaskIds });
      return { taskId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Failed to reorder subtasks");
    }
  }
);

// 5. Update subtask
export const updateSubtask = createAsyncThunk(
  "subtasks/updateSubtask",
  async ({ taskId, subtaskId, title, startDate, endDate, isCompleted, assigneeId }, { rejectWithValue }) => {
    try {
      const response = await axiosClientSignIn.put(`/tasks/subtasks/${subtaskId}`, {
        title,
        startDate,
        endDate,
        isCompleted,
        assigneeId
      });
      return { taskId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || "Failed to update subtask");
    }
  }
);

// 6. Delete subtask
export const deleteSubtask = createAsyncThunk(
  "subtasks/deleteSubtask",
  async ({ taskId, subtaskId }, { rejectWithValue }) => {
    try {
      await axiosClientSignIn.delete(`/tasks/subtasks/${subtaskId}`);
      return { taskId, subtaskId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.response?.data?.error || "Failed to delete subtask");
    }
  }
);

const subtaskSlice = createSlice({
  name: "subtasks",
  initialState: {
    itemsByTask: {}, // Stores an array of subtasks keyed by parent Task ID
    activityByTask: {}, // Stores enterprise audit logs keyed by parent Task ID
    loading: false,
    error: null,
  },
  reducers: {
    optimisticReorder: (state, action) => {
      const { taskId, items } = action.payload;
      state.itemsByTask[taskId] = items;
    },
    optimisticToggle: (state, action) => {
      const { taskId, subtaskId, isCompleted } = action.payload;
      if (state.itemsByTask[taskId]) {
        const index = state.itemsByTask[taskId].findIndex(t => t._id === subtaskId);
        if (index !== -1) {
          state.itemsByTask[taskId][index].isCompleted = isCompleted;
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSubtasks.pending, (state) => { state.loading = true; })
      .addCase(fetchSubtasks.fulfilled, (state, action) => {
        state.loading = false;
        state.itemsByTask[action.payload.taskId] = action.payload.data;
      })
      .addCase(fetchSubtasks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTaskActivity.fulfilled, (state, action) => {
        state.activityByTask[action.payload.taskId] = action.payload.data;
      })
      // Refresh activity log whenever a subtask is toggled via API
      .addCase(toggleSubtask.fulfilled, (state, action) => {
        const { taskId, subtaskId, data } = action.payload;
        const list = state.itemsByTask[taskId];
        if (!list) return;
        const index = list.findIndex((task) => task._id === subtaskId);
        if (index !== -1) {
          list[index] = data;
        }
      })
      .addCase(createSubtask.fulfilled, (state, action) => {
        const { taskId, data } = action.payload;
        if (!state.itemsByTask[taskId]) state.itemsByTask[taskId] = [];
        state.itemsByTask[taskId].push(data);
      })
      .addCase(updateSubtask.fulfilled, (state, action) => {
        const { taskId, data } = action.payload;
        const list = state.itemsByTask[taskId];
        if (!list) return;
        const index = list.findIndex((task) => task._id === data._id);
        if (index !== -1) {
          list[index] = data;
        }
      })
      .addCase(deleteSubtask.fulfilled, (state, action) => {
        const { taskId, subtaskId } = action.payload;
        if (state.itemsByTask[taskId]) {
          state.itemsByTask[taskId] = state.itemsByTask[taskId].filter((task) => task._id !== subtaskId);
        }
      });
  },
});

export const { optimisticReorder, optimisticToggle } = subtaskSlice.actions;
export default subtaskSlice.reducer;
